import React, { useEffect, useMemo, useState } from "react";

import type { ClientId } from "../../domain/types";
import { persistAnswer, useCoAnswer, useCoClient } from "../../data/store";
import { createRoundingOffLedger } from "../../data/roundingOffApi";
import {
  dbTrue,
  fetchZohoRecords,
  type ZohoLedgerDTO,
} from "../../data/zohoRecordsApi";
import { groupForType } from "../../domain/ledgers";
import { Callout } from "../../components/Callout";
import { CoButton } from "../../components/Button";
import { ComboPicker, type ComboOption } from "../../components/ComboPicker";
import { QuestionCard } from "../../components/QuestionCard";
import { SubStepShell } from "../../components/SubStepShell";
import { useSubStepNav } from "../useSubStepNav";

const ANSWER_KEY = "verifySetup.roundingOff";
const ROUND_OFF_NAME = "round off";

export interface RoundingOffAnswer {
  mode?: "mapped" | "created";
  accountId?: string;
  ledgerName?: string;
}

/**
 * Postable expense ledgers. A parent with children cannot take the paise
 * line Zoho categorizes as an expense.
 */
function isPostableExpense(ledger: ZohoLedgerDTO): boolean {
  return (
    groupForType(ledger.account_type) === "expense" &&
    dbTrue(ledger.is_active) &&
    !dbTrue(ledger.has_children)
  );
}

function isRoundOff(ledger: ZohoLedgerDTO): boolean {
  return (ledger.account_name ?? "").trim().toLowerCase() === ROUND_OFF_NAME;
}

export function RoundingOffStep({ clientId }: { clientId: ClientId }) {
  const client = useCoClient(clientId);
  const answer = useCoAnswer<RoundingOffAnswer>(clientId, ANSWER_KEY);
  const { goNext } = useSubStepNav(clientId, "rounding-off");

  const [ledgers, setLedgers] = useState<ZohoLedgerDTO[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    let live = true;
    setLoadError(null);

    fetchZohoRecords(clientId, ["ledgers"])
      .then((records) => {
        if (live) setLedgers(records.ledgers ?? []);
      })
      .catch((cause: unknown) => {
        if (live)
          setLoadError(
            cause instanceof Error
              ? cause.message
              : "Could not read the chart of accounts.",
          );
      });

    return () => {
      live = false;
    };
  }, [clientId]);

  const expenses = useMemo(
    () => (ledgers ?? []).filter(isPostableExpense),
    [ledgers],
  );
  const existingRoundOff = useMemo(
    () => expenses.find(isRoundOff) ?? null,
    [expenses],
  );

  const options = useMemo<ComboOption[]>(
    () =>
      expenses.map((ledger) => ({
        value: ledger.account_id,
        label: ledger.account_name ?? ledger.account_id,
        detail: ledger.account_code ?? undefined,
        search: [
          ledger.account_name ?? "",
          ledger.account_code ?? "",
          ledger.account_id,
        ],
      })),
    [expenses],
  );

  const chosen =
    expenses.find((ledger) => ledger.account_id === answer?.accountId) ?? null;
  const ready =
    answer?.mode === "created"
      ? Boolean(answer.accountId)
      : Boolean(answer?.accountId) && chosen !== null;

  function save(next: RoundingOffAnswer) {
    setActionError(null);
    void persistAnswer<RoundingOffAnswer>(clientId, ANSWER_KEY, next);
  }

  function selectMode(mode: "mapped" | "created") {
    if (mode === "mapped") {
      save({
        mode,
        accountId: answer?.mode === "mapped" ? answer.accountId : undefined,
        ledgerName: answer?.mode === "mapped" ? answer.ledgerName : undefined,
      });
      return;
    }

    if (existingRoundOff) {
      save({
        mode,
        accountId: existingRoundOff.account_id,
        ledgerName: existingRoundOff.account_name ?? "Round Off",
      });
      return;
    }

    if (answer?.mode === "created" && answer.accountId) {
      return;
    }

    save({ mode });
  }

  useEffect(() => {
    if (answer?.mode !== "created" || !existingRoundOff || answer.accountId)
      return;
    save({
      mode: "created",
      accountId: existingRoundOff.account_id,
      ledgerName: existingRoundOff.account_name ?? "Round Off",
    });
  }, [answer?.accountId, answer?.mode, existingRoundOff]);

  async function createLedger() {
    setCreating(true);
    setActionError(null);
    try {
      const account = await createRoundingOffLedger(clientId);
      const records = await fetchZohoRecords(clientId, ["ledgers"]);
      setLedgers(records.ledgers ?? []);
      save({
        mode: "created",
        accountId: account.account_id,
        ledgerName: account.account_name,
      });
    } catch (cause: unknown) {
      setActionError(
        cause instanceof Error ? cause.message : "Could not create Round Off.",
      );
    } finally {
      setCreating(false);
    }
  }

  if (!client) return null;

  const clientName = client.name || "this client";
  const missingSaved =
    answer?.mode === "mapped" &&
    Boolean(answer.accountId) &&
    ledgers !== null &&
    chosen === null
      ? "The saved ledger is no longer a postable expense account. Choose it again."
      : null;

  return (
    <SubStepShell
      title="Configure rounding off"
      description={
        <>
          Zournal posts small rounding differences to a single Round Off ledger in Zoho Books. These come from invoices, bills, journal entries, and bank reconciliations, and posting them to one expense account keeps every entry balanced.
          <br />
          <br />
          Ledger: Round Off, an expense account. This step adds it when the books do not already have it.
          <br />
          <br />
          You can replace it with another postable expense ledger. Ledgers outside the expense group can&apos;t be selected.
        </>
      }
      thenWhat={
        ready
          ? "Continue → GST Settings."
          : "Choose the rounding-off ledger to continue."
      }
      onContinue={() => void goNext()}
      continueDisabled={!ready || creating}
    >
      {loadError && <Callout variant="warning">{loadError}</Callout>}
      {missingSaved && <Callout variant="warning">{missingSaved}</Callout>}
      {actionError && <Callout variant="warning">{actionError}</Callout>}

      <QuestionCard
        headline="Which expense ledger should take rounding off?"
        whyLine="Map one they already use, or add Round Off to the chart of accounts."
        options={[
          {
            value: "mapped",
            title: "Use an existing expense ledger",
            description:
              "Point at the ledger they already post rounding differences to. Nothing is created in Zoho.",
          },
          {
            value: "created",
            title: "Create Round Off",
            description:
              "Add an expense account named Round Off. If that name is already in the books, we use it.",
          },
        ]}
        selected={answer?.mode}
        onSelect={(value) => selectMode(value as "mapped" | "created")}
      />

      {answer?.mode === "mapped" && (
        <div className="flex flex-col gap-2">
          {ledgers === null ? (
            <p className="text-sm text-muted-foreground">
              Reading the chart of accounts…
            </p>
          ) : expenses.length === 0 ? (
            <Callout variant="warning">
              No postable expense ledgers were read from {clientName}&apos;s
              books. Create Round Off, or refresh the chart of accounts on the
              previous step.
            </Callout>
          ) : (
            <ComboPicker
              options={options}
              value={answer?.accountId ?? ""}
              onChange={(accountId) => {
                const ledger = expenses.find(
                  (row) => row.account_id === accountId,
                );
                save({
                  mode: "mapped",
                  accountId,
                  ledgerName: ledger?.account_name ?? undefined,
                });
              }}
              placeholder="Select an expense ledger"
              searchPlaceholder="Search expense ledgers"
              noun="expense ledger"
            />
          )}
        </div>
      )}

      {answer?.mode === "created" && (
        <div className="flex flex-col gap-3">
          {answer?.accountId ? (
            <Callout variant="success">
              {chosen?.account_name ?? answer.ledgerName ?? "Round Off"} will be
              used as the rounding-off ledger.
            </Callout>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">
                This adds an expense account named Round Off in {clientName}
                &apos;s Zoho Books.
              </p>
              <CoButton
                onClick={() => void createLedger()}
                disabled={creating || ledgers === null}
              >
                {creating ? "Adding…" : "Create Round Off"}
              </CoButton>
            </>
          )}
        </div>
      )}
    </SubStepShell>
  );
}
